import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import styles from './Dashboard.module.scss';
import { faList, faBolt } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

import Header from 'components/Header/Header';
import Table from './Table/Table';
import AnyChart from 'anychart-react/dist/anychart-react';
import anychart from 'anychart';
import { useDatabaseSync } from 'shared/hooks';

import { Creators as pemCreators } from 'store/ducks/pems';
import { Creators as perguntasCreators } from 'store/ducks/perguntas';
import { Creators as respostasCreators } from 'store/ducks/respostas';
import { Creators as usersCreators } from 'store/ducks/users';
import { T, C } from 'shared/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const UsersCard = ({ type, count, all }) => (
  <div className={styles.CardContainer}>
    <div className={C(styles.Card, styles[`Card_${type}`])}>
      <header>
        <p>
          {type === 'created'
            ? 'Criados'
            : type === 'approved'
            ? 'Aprovados'
            : type === 'pending'
            ? 'Pendentes'
            : 'Reprovados'}
        </p>
      </header>
      <p>{count}</p>
      <footer>
        <p>
          {((count * 100) / all).toFixed(0)}%&nbsp;
          <FontAwesomeIcon icon={faBolt} />
        </p>
      </footer>
    </div>
  </div>
);

UsersCard.propTypes = {
  type: PropTypes.oneOf(['created', 'approved', 'pending', 'reproved'])
    .isRequired,
  all: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
};

const PEMPie = ({ pems }) => {
  const [anychartInstance, setAnychartInstance] = useState(undefined);
  const [chart, setChart] = useState(undefined);

  useEffect(() => {
    const data = anychart.data.set([
      ['Criadas', pems.todos],
      ['Finalizadas/Publicadas', pems.finished],
    ]);
    const wealth = data.mapAs({ x: 0, value: 1 });

    const chart = anychart.pie(wealth);
    chart.labels().hAlign('center').position('outside').format('{%value} ');

    // set chart title text settings

    // set legend title text settings
    chart
      .legend()
      // set legend position and items layout
      .position('center-bottom')
      .itemsLayout('horizontal')
      .align('center');

    setChart(chart);
  }, [pems, anychartInstance]);

  useEffect(() => {
    setAnychartInstance(anychart.graphics.create());
  }, []);

  return (
    <AnyChart
      id={Math.random().toString()}
      height={450}
      instance={anychartInstance}
      charts={[chart]}
    />
  );
};

PEMPie.propTypes = {
  pems: PropTypes.shape({
    finished: PropTypes.number,
    todos: PropTypes.number,
  }).isRequired,
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const syncs = useSelector((state) => state.syncs);
  const pems = useSelector((state) => state.pems.data);
  const cidadesLoading = useSelector((state) => state.cidades.loading);
  const pergunta = useSelector((state) =>
    state.perguntas.data.find((p) => p.code === 'status'),
  );
  const users = useSelector((state) => state.users.data);
  const currUser = useSelector((state) => state.user.data);
  const respostas = useSelector((state) => state.respostas.data);

  useEffect(() => {
    if (!cidadesLoading) {
      dispatch(pemCreators.getPems());
      dispatch(usersCreators.getUsers());
      dispatch(perguntasCreators.getPerguntas((p) => p.code === 'status'));
    }
  }, [dispatch, cidadesLoading]);
  useDatabaseSync('pems', () => pemCreators.getPems());
  useDatabaseSync('respostas', () => respostasCreators.getRespostas());
  useDatabaseSync('perguntas', () =>
    perguntasCreators.getPerguntas((p) => p.code === 'status'),
  );
  useEffect(() => {
    if (pems && pergunta) {
      dispatch(
        respostasCreators.getRespostas((r) => r.pergunta_id === pergunta.id),
      );
    }
  }, [pems, pergunta, dispatch]);

  function reducePems(pems) {
    const auxVariables = {
      regions: [],
      cultures: [],
    };

    return pems.reduce(
      (r, p) => {
        if (auxVariables.cultures.indexOf(p.cultura_id) === -1) {
          auxVariables.cultures.push(p.cultura_id);
          r.cultures++;
        }
        if (auxVariables.regions.indexOf(p.estado_id) === -1) {
          auxVariables.regions.push(p.estado_id);
          r.regions++;
        }
        return r;
      },
      { regions: 0, cultures: 0 },
    );
  }

  return (
    <div className={styles.content}>
      {users && users.length > 0 && (
        <>
          <Header icon={faList} title={'USUÁRIOS'} />
          <div className={C(styles.Row, styles.Block)}>
            <UsersCard type='created' count={users.length} all={users.length} />
            <UsersCard
              type='approved'
              count={users.filter((u) => u.situacao === 1).length}
              all={users.length}
            />
            <UsersCard
              type='pending'
              count={users.filter((u) => u.situacao === 2).length}
              all={users.length}
            />
            <UsersCard
              type='reproved'
              count={users.filter((u) => u.situacao === 3).length}
              all={users.length}
            />
          </div>
        </>
      )}
      <Header icon={faList} title={'PEM'} />
      {!syncs.loading && (
        <div className={styles.Row}>
          <Table
            data={
              users && users.length > 0
                ? users.map((u) => ({
                    user: u.name,
                    ...reducePems(pems.filter((p) => p.user_id === u.id)),
                  }))
                : [
                    {
                      user: currUser.name,
                      ...reducePems(pems),
                    },
                  ]
            }
          />
          <PEMPie
            pems={T(pems, []).reduce(
              (result, p) => {
                const respostaPem = T(respostas || []).find(
                  (r) =>
                    r.pem_id === T(p).id && T(r).pergunta_id === T(pergunta).id,
                );
                if (respostaPem) {
                  if (typeof respostaPem.resposta === 'string') {
                    respostaPem.resposta = JSON.parse(respostaPem.resposta);
                  }
                  if (respostaPem.resposta[0]) {
                    result.todos++;
                  } else {
                    result.finished++;
                  }
                } else {
                  result.todos++;
                }
                return result;
              },
              { finished: 0, todos: 0 },
            )}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
